import CloseIcon from '@material-ui/icons/Close';

import {
  Box,
  Button,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { OnClickAnchor } from "../../parts/OnClickAnchor";
import { MAP_ID_FLIGHT_HISTORY, mapContentsVisibleWithID } from '../../store/MapContentsState';
import {
  flightHistorySources,
  flightHistoryTargetState,
  flightHistoryWindowState,
  historyAircraftListState
} from '../../store/FlightHistoryState';
import { timeZoneSelector } from '../../store/TimeZoneState';
import { deepCopy, formatTimeZoneDate, formatTimeZoneStr, formatUTCDate } from '../../utils/FPEUtils';
import { Controller, useForm } from 'react-hook-form';
import GuideLink from "../GuideLink";
import { useLinkableSourceList } from '../../store/AircraftSettingState';

const useStyles = makeStyles(() => ({
  header: {
    cursor: "move",
  },
  createBtn: {
    display: "flex",
    float: "left",
    lineHeight: 1.6,
    margin: "2px 10px 2px 0",
    padding: "0 10px",
    backgroundColor: "#cfe2f3",
    color: "rgba(0, 0, 0, 0.87)",
    '&:hover': {
      backgroundColor: "#aad0f2",
    },
  },
  tableRoot: {
    '& .MuiTableCell-root': {
      border: 'none',
      padding: '2px',
      color: 'black',
    },
    '& .MuiTableCell-root:first-child': {
      paddingRight: '20px',
    },
    '& .MuiFormLabel-root': {
      color: 'black',
    }
  },
  radioRoot: {
    '& .MuiRadio-root': {
      paddingTop: '3px',
      paddingBottom: '3px',
    }
  },
  formInput: {
    '& .MuiOutlinedInput-input': {
      paddingTop: "5px",
      paddingBottom: "5px",
    },
    width: 190
  },
  formSpan: {
    display: 'flex',
    alignItems: 'baseline',
  },
  buttons: {
    display: 'flex'
  },
  button: {
    margin: 5
  },
  timeZone: {
    marginLeft: 10
  },
  displayNone: {
    display: 'none'
  },
  errorBox: {
    color: '#f44336',
    fontSize: '0.75rem',
    margin: '4px 14px 0 14px'
  },
  labelCell: {
    display: 'flex',
    marginTop: 7
  },
  widgetBody: {
    position: 'relative',
    padding: '5px 8px 8px 8px',
    background: '#fff',
    borderLeft: '1px solid #ccc',
    borderRight: '1px solid #ccc',
    borderBottom: '2px solid #ccc',
  },
  filterLow: {
    display: 'flex',
    alignItems: 'center'
  },
  filterLabel: {
    minWidth: 112,
    paddingRight: 20,
    textAlign: 'left'
  }
}));

const FlightHistoryPanel = () => {
  const [flightHistoryVisible, setFlightHistoryVisible] = useRecoilState(mapContentsVisibleWithID(MAP_ID_FLIGHT_HISTORY));
  return (
    <>
      {flightHistoryVisible && <FlightHistoryPanelComponent flightHistoryVisible={flightHistoryVisible} setFlightHistoryVisible={setFlightHistoryVisible}/>}
    </>
  )
}

export default FlightHistoryPanel; 

/* 飛行履歴画面 */
const FlightHistoryPanelComponent = (props) => {
  const {flightHistoryVisible, setFlightHistoryVisible} = props;
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const panelRef = useRef(null);
  const [type, setType] = useState('map');

  const setFlightHistoryTarget = useSetRecoilState(flightHistoryTargetState);
  const timeZone = useRecoilValue(timeZoneSelector);
  const timeZoneStr = formatTimeZoneStr(timeZone);
  const clickButtonType = useRef();
  const submitButton = useRef();
  const tempRangeDate = new Date();
  const latestDate = formatTimeZoneDate(tempRangeDate, timeZone, 'YYYY-MM-DDTHH:mm');
  tempRangeDate.setMonth(tempRangeDate.getMonth() - 3);
  const oldestDate = formatTimeZoneDate(tempRangeDate, timeZone, 'YYYY-MM-DDTHH:mm');
  const aircraftList = useRecoilValue(historyAircraftListState);
  const setFlightHistoryWindow = useSetRecoilState(flightHistoryWindowState);
  const [sources, setSources] = useRecoilState(flightHistorySources);
  const {linkableSourceList} = useLinkableSourceList();

  const {handleSubmit, setValue, getValues, control, formState: { errors }} = useForm({
    defaultValues: {
      dateRangeFrom: '',
      dateRangeTo: '',
      aircraft: ''
    }
  });

  const hidden = !flightHistoryVisible;

  const adjustPosition = (position) => {
    let { x, y } = position;
    if (panelRef.current.clientWidth > 0) {
      if (x + panelRef.current.clientWidth - 50 < 0) {
        x = -(panelRef.current.clientWidth - 50);
      }
    } else if (x < 0) {
      x = 0;
    }
    if (y < 0) {
      y = 0;
    }
    if (x > panelRef.current.parentElement.clientWidth - 10) {
      x = panelRef.current.parentElement.clientWidth - 30;
    }
    if (y > panelRef.current.parentElement.clientHeight - 10) {
      y = panelRef.current.parentElement.clientHeight - 30;
    }
    const changed = position.x !== x || position.y !== y;
    return { changed, position: { x, y } };
  };

  useEffect(() => {
    if (position) {
      const result = adjustPosition(position);
      if (result.changed) {
        setPosition(result.position);
      }
    }
  }, [position, setPosition]);

  const closeClickHandler = () => {
    setFlightHistoryVisible(!flightHistoryVisible);
  };

  const handleDragStop = (_, e) => {
    setPosition({ x: e.x, y: e.y });
  };

  const handleChangeType = event => {
    setType(event.target.value);
  }

  const classes = useStyles();
  const { t } = useTranslation();

  const changeTimeZone = useCallback((dateStr) => {
    if (!dateStr) return '';
    let ret;
    if (timeZone === 'Asia/Tokyo') {
      ret = formatTimeZoneDate(dateStr, timeZone, 'YYYY-MM-DDTHH:mm');
    } else {
      ret = formatUTCDate(new Date(dateStr), '%Y-%m-%dT%H:%M')
    }
    return ret;
  }, [timeZone]);

  useEffect(() => {
    const values = getValues();
    setValue('dateRangeFrom', changeTimeZone(values.dateRangeFrom));
    setValue('dateRangeTo', changeTimeZone(values.dateRangeTo));
  }, [changeTimeZone, getValues, setValue]);

  useEffect(() => {
    const date = new Date();
    date.setHours(date.getHours() - 1);
    setValue('dateRangeTo', formatTimeZoneDate(date, timeZone, 'YYYY-MM-DDTHH:mm'));
    let ago = 6;  // 元日付からは７時間前
    if (type === 'csv') {
      ago = 24;   // 元日付からは25時間前
    }
    date.setHours(date.getHours() - ago);
    setValue('dateRangeFrom', formatTimeZoneDate(date, timeZone, 'YYYY-MM-DDTHH:mm'));

  // eslint-disable-next-line
  }, [type, setValue]);

  const submit = () => {
    const values = getValues();
    let from = values.dateRangeFrom;
    let to = values.dateRangeTo;
    if (timeZoneStr === 'JST') {
      from = formatUTCDate(new Date(from), '%Y-%m-%dT%H:%M');
      to = formatUTCDate(new Date(to), '%Y-%m-%dT%H:%M');
    }
    setFlightHistoryTarget({
      type: clickButtonType.current,
      from: from + 'Z',
      to: to + 'Z',
      aircraft_code: values.aircraft
    });

    if (clickButtonType.current === 'map') {
      setFlightHistoryWindow([]);
    }
  }

  const validationRules = {
    dateRangeFrom: {
      validate: {
        message: (v) => {
          // from の必須チェック
          if (!v || v.length === 0) {
            return t('validate_required')
          }

          return null;
        }
      }
    },
    dateRangeTo: {
      validate: {
        message: (v) => {
          // to の必須チェック
          if (!v || v.length === 0) {
            return t('validate_required')
          }

          const from = new Date(getValues().dateRangeFrom).getTime();
          const to = new Date(v).getTime();
          // from toの逆転チェック
          if (to < from) {
            return t('Please enter a start datetime or later.');
          }

          // 範囲チェック
          if (type === 'csv') {
            if (24 * 60 * 60 * 1000 < to - from) {
              return t('[CSV] Please enter a period within 24 hours.');
            }
          } else {
            if (6 * 60 * 60 * 1000 < to - from) {
              return t('[MAP] Please enter a period within 6 hours.');
            }
          }
          
          return null;
        }
      }
    },
    aircraft: {
      required: t('validate_required')
    }
  }

  const handleButtonClick = event => {
    clickButtonType.current = event.currentTarget.value;
    submitButton.current.click();
  }

  const handleToggleVisible = (e, target) => {
    setSources(prev => {
      const newData = deepCopy(prev);
      newData.find(d => d.dataSourceId === target).visible = e.target.checked;
      return newData;
    });
    setFlightHistoryWindow(prev => {
      if (!e.target.checked) {
        return prev.filter(f => f.properties.data_source_id !== target)
      }
      return prev;
    });
  }

  return (
    <Draggable handle=".widget-header" onStop={handleDragStop} position={position} cancel={`.${classes.createBtn}, .close-anchor, .widget-guid-link`}>
      <div className={clsx("widget-box", "flight-plan", "dark-color")} hidden={hidden} ref={panelRef}>
        <div className={clsx("widget-header", classes.header)}><h4>{t(`Flight History`)}</h4>
          <div className="widget-guid-link">
            <GuideLink id={MAP_ID_FLIGHT_HISTORY}/>
          </div>
          <div className="widget-toolbar">
            <OnClickAnchor onClick={closeClickHandler} className="close-anchor">
              <Tooltip title="Close" arrow >
                <CloseIcon />
              </Tooltip>
            </OnClickAnchor>
          </div>
        </div>
        <div id="flightplan-widget-body" className={classes.widgetBody}>
          <form onSubmit={handleSubmit(submit)}>
            <Table padding='none' className={classes.tableRoot}>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.labelCell}>
                    <FormLabel>{t('Output Format')}</FormLabel>
                  </TableCell>
                  <TableCell>
                    <RadioGroup row aria-label="position" name="position" value={type} onChange={handleChangeType} className={classes.radioRoot}>
                      <FormControlLabel value="map" control={<Radio color="primary" />} label="MAP" />
                      <FormControlLabel value="csv" control={<Radio color="primary" />} label="CSV" />
                    </RadioGroup>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.labelCell}>
                    <FormLabel>{t('period')} ({timeZoneStr})</FormLabel>
                  </TableCell>
                  <TableCell>
                    <Box className={classes.formSpan}>
                      <Controller
                        name='dateRangeFrom'
                        control={control}
                        rules={validationRules.dateRangeFrom}
                        render={({field}) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            className={classes.formInput}
                            size="small"
                            inputProps={{
                              type: "datetime-local",
                              min: oldestDate,
                              max: latestDate
                            }}
                            error={'dateRangeFrom' in errors}
                            helperText={errors.dateRangeFrom?.message}
                            />
                        )}
                      />
                      <FormLabel>～</FormLabel>
                      <Controller
                        name='dateRangeTo'
                        control={control}
                        rules={validationRules.dateRangeTo}
                        render={({field}) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            className={classes.formInput}
                            size="small"
                            inputProps={{
                              type: "datetime-local",
                              min: oldestDate,
                              max: latestDate
                            }}
                            error={'dateRangeTo' in errors}
                            helperText={errors.dateRangeTo?.message}
                            />
                        )}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.labelCell}>
                    <FormLabel>{t('Aircraft Code')}</FormLabel>
                  </TableCell>
                  <TableCell>
                    <Controller
                      name='aircraft'
                      control={control}
                      rules={validationRules.aircraft}
                      render={({field}) => (
                        <Select
                          {...field}
                          variant="outlined"
                          className={classes.formInput}
                          error={'aircraft' in errors}
                        >
                          {
                            aircraftList.map(aircraft => {
                              return <MenuItem key={`${aircraft}-flight-history`} value={aircraft}>{aircraft}</MenuItem>
                            })
                          }
                        </Select>
                      )}
                    />
                    <Box className={classes.errorBox}>
                      {errors.aircraft && t(errors.aircraft.message)}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell className={classes.buttons}>
                    <Button className={classes.displayNone} type='submit' ref={submitButton}></Button>
                    <Button className={classes.button} variant="contained" size="small" value='map' onClick={handleButtonClick} disabled={type !== 'map'} >{t('Map View')}</Button>
                    <Button className={classes.button} variant="contained" size="small" value='csv' onClick={handleButtonClick} disabled={type !== 'csv'}>{t('Track Export')}</Button>
                    <Button className={classes.button} variant="contained" size="small" value='alert' onClick={handleButtonClick} disabled={type !== 'csv'}>{t('Alert Export')}</Button>
                    <Button className={classes.button} disabled variant="contained" size="small">{t('Message Export')}</Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </form>
          {
            sources.length > 1 &&
            <div>
              {
                sources.map(s => {
                  return <div className={classes.filterLow} key={s.dataSourceId}>
                    <div className={classes.filterLabel}>
                      {linkableSourceList.find(l => l.data_source_id === s.dataSourceId)?.data_source_name}
                    </div>
                    <div>
                      <FormControlLabel
                        control={<Switch checked={s.visible} onChange={e => handleToggleVisible(e, s.dataSourceId)} color="primary" />}
                        label={t("Show")}
                      />
                    </div>
                  </div>
                })
              }
            </div>
          }
        </div>
      </div>
    </Draggable>
  );
};
